import { defineStore } from 'pinia'
import { db } from '@/services/firebase/config'
import { getDoc, setDoc, updateDoc, doc, serverTimestamp, deleteField } from 'firebase/firestore'
import { targetedCloneForMutation } from '@/utils/cloneHelpers'

export const usePlansStore = defineStore('plans', {
  state: () => ({
    plans: [],
    loading: false,
    error: null,
    lastSyncedAt: null
  }),

  getters: {
    orderedPlans: (state) => [...state.plans].sort((a, b) => (b.priority || 0) - (a.priority || 0))
  },

  actions: {
    // Normalize input into the canonical schema written to Firestore
    normalizePlanForSave(input) {
      const src = { ...(input || {}) }
      const id = String(src.id || '').trim()
      if (!id) throw new Error('Plan id required')

      const meta = {
        id,
        name: src.name ?? '',
        shortName: src.shortName ?? '',
        description: src.description ?? '',
        color: src.color ?? '',
        priceMonthEUR: Number(src.priceMonthEUR ?? 0),
        priceYearEUR: Number(src.priceYearEUR ?? 0),
        priority: Number(src.priority ?? 0),
        hasExpiration: !!src.hasExpiration
      }

      // Canonical locations only
      const storage = {
        maxStorageMB: Number(src.features?.storage?.maxStorageMB ?? 0)
      }
      const gallery = {
        characterGallery: {
          maxImages: Number(src.features?.gallery?.characterGallery?.maxImages ?? 0)
        },
        campaignGallery: {
          maxImages: Number(src.features?.gallery?.campaignGallery?.maxImages ?? 0)
        }
      }
      const aiEnabled = !!(src.features?.ai?.enabled ?? false)
      const usage = src.features?.ai?.usage || {}
      // Count-based limits (ACTIVE)
      const campaignDescription = Number(usage.campaignDescription ?? 0)
      const characterDescription = Number(usage.characterDescription ?? 0)
      const characterImport = Number(usage.characterImport ?? 0)
      const audioTranscription = Number(usage.audioTranscription ?? 0)
      const audioGeneration = Number(usage.audioGeneration ?? 0)
      const videoGeneration = Number(usage.videoGeneration ?? 0)

      const imageGenerationSrc = usage.imageGeneration || {}
      const imageGeneration = {
        characterAvatar: Number(imageGenerationSrc.characterAvatar ?? 0),
        characterGallery: Number(imageGenerationSrc.characterGallery ?? 0),
        campaignImage: Number(imageGenerationSrc.campaignImage ?? 0)
      }

      return {
        ...meta,
        features: {
          storage,
          gallery,
          ai: {
            enabled: aiEnabled,
            usage: {
              // Count-based limits (ACTIVE)
              campaignDescription,
              characterDescription,
              characterImport,
              audioTranscription,
              audioGeneration,
              videoGeneration,
              imageGeneration
            }
          }
        }
      }
    },
    async fetchPlans() {
      this.loading = true
      this.error = null
      try {
        const ref = doc(db, 'config', 'plans')
        const snap = await getDoc(ref)
        if (!snap.exists()) {
          this.plans = []
          return this.plans
        }
        const data = snap.data() || {}
        const list = Object.entries(data)
          .filter(([k]) => k !== 'updatedAt')
          .map(([id, plan]) => targetedCloneForMutation({ id, ...plan }))
        this.plans = list
        return this.plans
      } catch (e) {
        this.error = e.message || String(e)
        throw e
      } finally {
        this.loading = false
      }
    },

    async upsertPlan(plan) {
      if (!plan?.id) throw new Error('Plan id required')
      const ref = doc(db, 'config', 'plans')
      const canonical = this.normalizePlanForSave(plan)
      await updateDoc(ref, {
        [canonical.id]: { ...canonical, updatedAt: serverTimestamp() },
        updatedAt: serverTimestamp()
      })
      await this.fetchPlans()
    },

    async deletePlan(id) {
      const ref = doc(db, 'config', 'plans')
      await updateDoc(ref, { [id]: deleteField(), updatedAt: serverTimestamp() })
      await this.fetchPlans()
    },

    async syncFromConfig() {
      this.loading = true
      this.error = null
      try {
        // No external config file to sync from anymore; keep as no-op
        // Optionally ensure the document exists
        const ref = doc(db, 'config', 'plans')
        const snap = await getDoc(ref)
        if (!snap.exists()) {
          await setDoc(ref, { updatedAt: serverTimestamp() }, { merge: true })
        }
        this.lastSyncedAt = new Date().toISOString()
        await this.fetchPlans()
        return { synced: 0 }
      } catch (e) {
        this.error = e.message || String(e)
        throw e
      } finally {
        this.loading = false
      }
    }
  }
})
